import { WORLD } from "@clubap/shared";
import { Container, Graphics } from "pixi.js";

/**
 * Las piezas que se repiten en el campus, dibujadas una sola vez.
 *
 * Nacieron al agregar doce salas de golpe: cada escenario volvía a pintar su cielo,
 * su césped y sus baldosas, y el segundo que se retocaba a mano ya no cuadraba con el
 * primero. Un campus se ve como un campus cuando el suelo es el mismo suelo.
 *
 * Lo que NO va aquí es lo que distingue a cada sitio — el bus del paradero, el
 * mostrador del Sweet, las gradas del coliseo. Eso vive en su propio archivo, que es
 * donde se lee de un vistazo qué hace único a ese lugar.
 */

/** El horizonte de las salas al aire libre. Todo lo de arriba es fondo, no piso. */
export const HORIZON = 250;

/** El arranque del piso en interiores: por encima es pared. */
export const WALL = 255;

export interface OutdoorOptions {
  /** El suelo: césped, asfalto o baldosa, según el sitio. */
  ground: number;
  sky?: number;
  /** Franja de vegetación contra el horizonte. Omitirla deja el borde limpio. */
  hedge?: number;
}

/**
 * Base de exterior: cielo, línea de horizonte y suelo.
 *
 * El suelo se dibuja hasta el borde inferior del mundo y no hasta el polígono
 * caminable: el piso pintado tiene que sobrar por todos lados, o se ve el fondo
 * asomando justo donde el jugador llega al límite.
 */
export function outdoor({ ground, sky = 0x9ec9e8, hedge }: OutdoorOptions): Container {
  const scene = new Container();

  const base = new Graphics();
  base.rect(0, 0, WORLD.width, HORIZON).fill(sky);
  base.rect(0, HORIZON, WORLD.width, WORLD.height - HORIZON).fill(ground);
  scene.addChild(base);

  if (hedge !== undefined) {
    const green = new Graphics();
    green.rect(0, HORIZON - 26, WORLD.width, 26).fill(hedge);
    scene.addChild(green);
  }

  return scene;
}

export interface IndoorOptions {
  wall: number;
  floor: number;
  /** Zócalo entre pared y piso. Es lo que hace que la pared se lea como pared. */
  skirting?: number;
}

/** Base de interior: pared, zócalo y piso con baldosas en perspectiva simple. */
export function indoor({ wall, floor, skirting = 0xc2cfdc }: IndoorOptions): Container {
  const scene = new Container();

  const walls = new Graphics();
  walls.rect(0, 0, WORLD.width, WALL).fill(wall);
  walls.rect(0, WALL - 12, WORLD.width, 12).fill(skirting);
  scene.addChild(walls);

  const tiles = new Graphics();
  tiles.rect(0, WALL, WORLD.width, WORLD.height - WALL).fill(floor);

  /*
   * Las líneas convergen hacia el centro, no son paralelas.
   *
   * Es el mismo truco del laboratorio: con un punto de fuga el suelo se lee como
   * profundidad en vez de como un rectángulo de color, y cuesta seis líneas.
   */
  for (let i = 0; i <= 12; i++) {
    const x = (WORLD.width / 12) * i;
    tiles.moveTo(x, WALL).lineTo(x + (x - WORLD.width / 2) * 0.35, WORLD.height);
    tiles.stroke({ width: 1, color: 0x000000, alpha: 0.08 });
  }
  for (let i = 1; i < 5; i++) {
    const y = WALL + (WORLD.height - WALL) * (i / 5) ** 1.4;
    tiles.moveTo(0, y).lineTo(WORLD.width, y).stroke({ width: 1, color: 0x000000, alpha: 0.08 });
  }

  scene.addChild(tiles);
  return scene;
}

/** Una fila de ventanas altas, para que un interior no sea una caja cerrada. */
export function windows(scene: Container, count: number, y = 56, tint = 0x9fd3ea): void {
  const step = WORLD.width / count;

  for (let i = 0; i < count; i++) {
    const frame = new Graphics();
    const x = step * i + step / 2 - 65;
    frame.rect(x, y, 130, 108).fill(tint).stroke({ width: 4, color: 0xf4f7fa });
    frame.moveTo(x + 65, y).lineTo(x + 65, y + 108).stroke({ width: 3, color: 0xf4f7fa });
    scene.addChild(frame);
  }
}

/**
 * Un bloque de edificio contra el horizonte, con sus ventanas.
 *
 * Es la pieza que hace legible una sala exterior: sin edificios al fondo, una
 * plazoleta y un parqueo son el mismo rectángulo de color con otro nombre.
 */
export function building(
  x: number,
  y: number,
  width: number,
  height: number,
  face = 0xe6ded2,
): Container {
  const block = new Container();

  const body = new Graphics();
  body.rect(x, y, width, height).fill(face).stroke({ width: 3, color: 0x9c9384 });
  block.addChild(body);

  const glass = new Graphics();
  const columns = Math.max(2, Math.floor(width / 55));
  const rows = Math.max(1, Math.floor(height / 55));

  for (let c = 0; c < columns; c++) {
    for (let r = 0; r < rows; r++) {
      glass.rect(x + 14 + c * (width / columns), y + 16 + r * (height / rows), 26, 26);
    }
  }
  glass.fill({ color: 0x6fa8c8, alpha: 0.85 });
  block.addChild(glass);

  return block;
}

/** Un árbol. Copa redonda y tronco, que a esta escala es todo lo que hace falta. */
export function tree(x: number, groundY: number, scale = 1): Container {
  const t = new Container();

  const trunk = new Graphics();
  trunk.rect(x - 6 * scale, groundY - 46 * scale, 12 * scale, 46 * scale).fill(0x7a5a3a);
  t.addChild(trunk);

  const crown = new Graphics();
  crown.circle(x, groundY - 62 * scale, 34 * scale).fill(0x3f8f4f);
  crown.circle(x - 22 * scale, groundY - 46 * scale, 24 * scale).fill(0x4c9c5b);
  crown.circle(x + 22 * scale, groundY - 46 * scale, 24 * scale).fill(0x37814a);
  t.addChild(crown);

  return t;
}

/** Una banca de exterior, vista de frente. */
export function bench(x: number, y: number, width = 90): Container {
  const b = new Container();

  const seat = new Graphics();
  seat.rect(x, y, width, 12).fill(0x8b6b45);
  seat.rect(x, y - 22, width, 10).fill(0x8b6b45);
  seat.rect(x + 4, y + 12, 8, 18).fill(0x5a5a5a);
  seat.rect(x + width - 12, y + 12, 8, 18).fill(0x5a5a5a);
  b.addChild(seat);

  return b;
}
